import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiMail, FiCheck, FiCheckCircle, FiTrash2, FiEye, FiDownload } from 'react-icons/fi';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState(null);

  const loadData = async () => {
    try {
      const res = await adminAPI.getContacts({ page, pageSize: 15 });
      if (res.success) { setContacts(res.data.items); setTotalPages(res.data.totalPages); }
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [page]);

  const handleMarkRead = async (id) => {
    try { await adminAPI.markAsRead(id); loadData(); }
    catch (err) { toast.error('Failed'); }
  };

  const handleMarkReplied = async (id) => {
    try { await adminAPI.markAsReplied(id); toast.success('Marked as replied'); loadData(); }
    catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact request?')) return;
    try { await adminAPI.deleteContact(id); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error('Failed'); }
  };

  const exportExcel = () => {
    const csv = [
      ['Name', 'Email', 'Subject', 'Message', 'Date', 'Read', 'Replied'].join(','),
      ...contacts.map(c => [
        `"${c.name}"`, `"${c.email}"`, `"${c.subject || ''}"`, `"${c.message}"`,
        formatDate(c.createdAt), c.isRead ? 'Yes' : 'No', c.isReplied ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'contacts.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Contacts | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <h2>Contact Requests</h2>
        <button className="btn btn-outline" onClick={exportExcel}><FiDownload /> Export CSV</button>
      </div>

      <div className="manage-table">
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Subject</th><th>Date</th><th>Read</th><th>Replied</th><th>Actions</th></tr></thead>
          <tbody>
            {contacts.map(c => (
              <tr key={c.id} style={{fontWeight: c.isRead ? 'normal' : '600'}}>
                <td>{c.name}</td>
                <td>{c.email}</td>
                <td>{c.subject || '-'}</td>
                <td style={{fontSize:'0.85rem', color:'var(--text-muted)'}}>{formatDate(c.createdAt)}</td>
                <td>{c.isRead ? <FiCheck color="var(--success)" /> : <FiEye color="var(--warning)" />}</td>
                <td>{c.isReplied ? <FiCheckCircle color="var(--success)" /> : '-'}</td>
                <td>
                  <div className="action-btns">
                    <button className="btn-icon edit" onClick={() => setSelected(c)} title="View"><FiEye /></button>
                    {!c.isRead && <button className="btn-icon" style={{background:'rgba(0,184,148,0.1)', color:'#00b894'}} onClick={() => handleMarkRead(c.id)} title="Mark Read"><FiCheck /></button>}
                    <button className="btn-icon" style={{background:'rgba(26,115,232,0.1)', color:'var(--primary)'}} onClick={() => handleMarkReplied(c.id)} title="Mark Replied"><FiCheckCircle /></button>
                    <button className="btn-icon delete" onClick={() => handleDelete(c.id)}><FiTrash2 /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination" style={{marginTop: 20}}>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} className={`page-btn ${page === i+1 ? 'active' : ''}`} onClick={() => setPage(i+1)}>{i+1}</button>
          ))}
        </div>
      )}

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Contact Details</h2>
            <div style={{display:'grid', gap:'16px'}}>
              <div><strong>Name:</strong> {selected.name}</div>
              <div><strong>Email:</strong> <a href={`mailto:${selected.email}`}>{selected.email}</a></div>
              <div><strong>Subject:</strong> {selected.subject || 'N/A'}</div>
              <div><strong>Date:</strong> {formatDate(selected.createdAt)}</div>
              <div><strong>Status:</strong> {selected.isRead ? 'Read' : 'Unread'} | {selected.isReplied ? 'Replied' : 'Not Replied'}</div>
              <div><strong>Message:</strong></div>
              <div style={{padding:'16px', background:'var(--bg-secondary)', borderRadius:'8px', lineHeight:1.6}}>{selected.message}</div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={() => { handleMarkRead(selected.id); handleMarkReplied(selected.id); setSelected(null); }}>
                Mark as Read & Replied
              </button>
              <button className="btn btn-outline" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageContacts;
